import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { useState, useCallback } from 'react';
import { isElementDomNode } from '@remirror/core';
import { useRemirrorContext } from '@remirror/react-core';
import { useEvent } from '../../use-event/dist/remirror-react-hooks-use-event.browser.esm.js';
import '@remirror/extension-events';

/**
 * Keep track of the editor focus.
 *
 * Returns a focused value which is updated whenever the editor focus changes.
 *
 * When `true`, the editor is focused when `false` the editor is not focused.
 */
function useEditorFocus() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var _props$ignoredElement = props.ignoredElements,
      ignoredElements = _props$ignoredElement === void 0 ? [] : _props$ignoredElement,
      _props$blurOnInactive = props.blurOnInactive,
      blurOnInactive = _props$blurOnInactive === void 0 ? false : _props$blurOnInactive; // Get the view from the context and use it to calculate the initial focus.

  var _useRemirrorContext = useRemirrorContext(),
      view = _useRemirrorContext.view,
      commands = _useRemirrorContext.commands; // Create the initial state with the current view focus.


  var _useState = useState(() => view.hasFocus()),
      _useState2 = _slicedToArray(_useState, 2),
      isFocused = _useState2[0],
      setIsFocused = _useState2[1]; // Listen to blur events and set focused to false in those instances.


  useEvent('blur', useCallback(event => {
    var focusedElement = isElementDomNode(event.target) ? event.target : document.activeElement;
    var ignoreBlur = !blurOnInactive && !focusedElement;

    if (ignoreBlur || view.dom.contains(focusedElement)) {
      return false;
    }

    var _iterator = _createForOfIteratorHelper(ignoredElements),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var element = _step.value;

        if (element !== null && element !== void 0 && element.contains(focusedElement)) {
          return false;
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    setIsFocused(false);
    return false;
  }, [blurOnInactive, ignoredElements, view.dom])); // Listen to focus events and set focused to true in those instances.

  useEvent('focus', useCallback(_ => {
    if (isFocused) {
      return false;
    }

    setIsFocused(true);
    return false;
  }, [isFocused]));
  return [isFocused, commands.focus];
}

export { useEditorFocus };
